import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import React from "react";
import {
  ILeadFoodMenuCategoryPreviewData,
  ILeadFoodMenuFoodItemPreviewData,
  ILeadFoodMenuPreviewData,
} from "../views/leads/LeadFoodMenu/LeadFoodMenu.types";
import { useTimezoneFormatter } from "../hooks/useTimezoneFormatter";
import moment from "moment";
import companyLogo from "../assets/banquet_first.png"
// Fonts — using local NotoSans variants
const NotoSansFont = require("./fonts/NotoSans-Regular.ttf");
const NotoSansBold = require("./fonts/NotoSans-Bold.ttf");
const NotoSansSemiBold = require("./fonts/NotoSans-SemiBold.ttf");
const NotoSansExtraBold = require("./fonts/NotoSans-ExtraBold.ttf");

Font.register({ family: "NotoSans", src: NotoSansFont });
Font.register({ family: "NotoSans", fontWeight: 600, src: NotoSansSemiBold });
Font.register({ family: "NotoSans", fontWeight: "bold", src: NotoSansBold });
Font.register({ family: "NotoSans", fontWeight: 800, src: NotoSansExtraBold });

// Color palette
const NAVY = "#0D3B66";
const TEXT_PRIMARY = "#1a1a18";
const TEXT_SECONDARY = "#777777";
const TEXT_LIGHT = "#aaaaaa";
const GREEN = "#2e7d32";

// Food type icon colors
const FOOD_ICON_COLORS: Record<string, string> = {
  veg: "#22C55E",
  nonveg: "#EF4444",
  egg: "#FACC15",
  halal: "#F59E0B",
  vegan: "#4CAF50",
};

const BORDER_MARGIN = 15;
const BORDER_GAP = 5;

const styles = StyleSheet.create({
  page: {
    paddingVertical: 55,
    paddingHorizontal: 65,
    fontFamily: "NotoSans",
    fontSize: 10,
    backgroundColor: "#ffffff",
    color: TEXT_PRIMARY,
  },

  // Double border (fixed = repeats on every page)
  outerBorder: {
    position: "absolute",
    top: BORDER_MARGIN,
    left: BORDER_MARGIN,
    right: BORDER_MARGIN,
    bottom: BORDER_MARGIN,
    borderWidth: 1.5,
    borderColor: NAVY,
  },

  innerBorder: {
    position: "absolute",
    top: BORDER_MARGIN + BORDER_GAP,
    left: BORDER_MARGIN + BORDER_GAP,
    right: BORDER_MARGIN + BORDER_GAP,
    bottom: BORDER_MARGIN + BORDER_GAP,
    borderWidth: 0.5,
    borderColor: NAVY,
  },

  // Top logos row
  topLogoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 14,
  },

  cornerLogoBlock: {
    alignItems: "flex-start",
    width: 130,
  },

  cornerLogoBlockRight: {
    alignItems: "flex-end",
    width: 130,
  },

  cornerLogoImage: {
    width: 80,
    height: 50,
    objectFit: "contain",
  },

  cornerCompanyName: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: NAVY,
    letterSpacing: 0.8,
    textAlign: "center",
    marginTop: 4,
  },

  // Header
  menuTitle: {
    fontSize: 28,
    fontWeight: 800,
    color: TEXT_PRIMARY,
    textAlign: "center",
    marginBottom: 6,
  },

  eventSubtitle: {
    fontSize: 11,
    color: TEXT_SECONDARY,
    textAlign: "center",
  },

  // Diamond divider
  diamondDividerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 14,
    marginBottom: 10,
  },

  diamondLine: {
    width: 40,
    height: 1,
    backgroundColor: NAVY,
    opacity: 0.3,
  },

  diamond: {
    width: 7,
    height: 7,
    backgroundColor: NAVY,
    marginHorizontal: 8,
    transform: "rotate(45deg)",
  },

  // Stats row
  statsRow: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 20,
    marginBottom: 20,
  },

  statText: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: TEXT_SECONDARY,
    letterSpacing: 1.2,
  },

  // Course / Category
  courseBlock: {
    marginTop: 24,
    marginBottom: 8,
  },

  courseLabel: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: NAVY,
    letterSpacing: 2,
    textAlign: "center",
    marginBottom: 4,
  },

  courseName: {
    fontSize: 18,
    fontWeight: 800,
    color: NAVY,
    textAlign: "center",
  },

  // Subcategory
  subCategoryLabel: {
    fontSize: 8,
    fontWeight: "bold",
    color: TEXT_SECONDARY,
    letterSpacing: 1,
    marginTop: 12,
    marginBottom: 4,
    marginLeft: 24,
  },

  // Food item
  foodItemRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 6,
    paddingLeft: 24,
    paddingRight: 20,
  },

  foodItemContent: {
    flex: 1,
  },

  foodItemName: {
    fontSize: 12,
    fontWeight: 600,
    color: TEXT_PRIMARY,
  },

  foodItemDesc: {
    fontSize: 9,
    color: TEXT_SECONDARY,
    marginTop: 1.5,
  },

  foodItemTag: {
    fontSize: 7,
    fontWeight: "bold",
    color: TEXT_LIGHT,
    letterSpacing: 1,
    alignSelf: "center",
  },
});

/**
 * Helper interfaces
 */
interface ISubCategoryWithItems {
  subcategory_name: string;
  items: ILeadFoodMenuFoodItemPreviewData[];
}

interface ICategoryGroupedData {
  category: ILeadFoodMenuCategoryPreviewData;
  subcategories: ISubCategoryWithItems[];
  orphanItems: ILeadFoodMenuFoodItemPreviewData[];
}

function formatDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  try {
    return moment(dateStr).format("DD MMM YYYY");
  } catch {
    return dateStr;
  }
}

export const LeadFoodMenuEjs: React.FC<{
  data: ILeadFoodMenuPreviewData;
}> = ({ data }) => {
  const { company, lead, menu } = data;
  const { ftDate } = useTimezoneFormatter();

  const outletName: string = company?.name || "";
  const outletLogo: string | null = company?.logo || null;

  const processCategory = (
    category: ILeadFoodMenuCategoryPreviewData,
  ): ICategoryGroupedData => {
    const allItems = category.food_items || [];

    if (category.subcategories?.length > 0) {
      const subcategories: ISubCategoryWithItems[] = [];
      const placedItems = new Set<ILeadFoodMenuFoodItemPreviewData>();

      for (const sub of category.subcategories) {
        const subUuid = (sub as any).food_subcategory_name_uuid;
        // Fallback membership set for legacy items that lack sub_category_uuid
        const subItemUuids = new Set(
          (sub.food_items || []).map((fi: any) => fi.food_item_uuid),
        );

        const matchedItems = allItems.filter((item) => {
          if (placedItems.has(item)) return false;
          // Primary: match this placement's own sub_category_uuid
          if (item.sub_category_uuid) {
            return item.sub_category_uuid === subUuid;
          }
          // Fallback (legacy data): match by uuid membership in the sub
          return subItemUuids.has(item.food_item_uuid);
        });

        if (matchedItems.length > 0) {
          subcategories.push({
            subcategory_name: sub.subcategory_name,
            items: matchedItems,
          });
          matchedItems.forEach((item) => placedItems.add(item));
        }
      }

      const orphanItems = allItems.filter((item) => !placedItems.has(item));

      return { category, subcategories, orphanItems };
    }

    // No declared subcategories on the category — group by item metadata
    const subCategoryMap = new Map<string, ILeadFoodMenuFoodItemPreviewData[]>();
    const orphanItems: ILeadFoodMenuFoodItemPreviewData[] = [];

    allItems.forEach((item) => {
      let foundInSub = false;

      const categoryMeta = item.category_list?.find(
        (c) => c.category_uuid === category.category_uuid,
      );

      if (categoryMeta && categoryMeta.subcategories?.length > 0) {
        for (const sub of categoryMeta.subcategories) {
          // Primary: match this placement's own sub_category_uuid
          const matchesByPlacement =
            item.sub_category_uuid &&
            (sub as any).food_subcategory_name_uuid === item.sub_category_uuid;
          // Fallback (legacy): match by uuid membership
          const matchesByMembership =
            !item.sub_category_uuid &&
            sub.food_items?.some(
              (subItem: any) => subItem.food_item_uuid === item.food_item_uuid,
            );

          if (matchesByPlacement || matchesByMembership) {
            const existing = subCategoryMap.get(sub.subcategory_name) || [];
            existing.push(item);
            subCategoryMap.set(sub.subcategory_name, existing);
            foundInSub = true;
            break;
          }
        }
      }

      if (!foundInSub) {
        orphanItems.push(item);
      }
    });

    const subcategories: ISubCategoryWithItems[] = Array.from(
      subCategoryMap.entries(),
    ).map(([name, items]) => ({
      subcategory_name: name,
      items,
    }));

    return { category, subcategories, orphanItems };
  };

  const categoriesToRender =
    menu?.categories?.map((c) => processCategory(c)) || [];

  const totalDishes = categoriesToRender.reduce(
    (sum, g) => sum + g.category.food_items.length, 0,
  );
  const totalCourses = categoriesToRender.length;
  const totalVeg = categoriesToRender.reduce(
    (sum, g) => sum + g.category.food_items.filter((i) => i.food_type === "veg").length, 0,
  );

  const formattedDate = lead?.event_date ? formatDate(lead.event_date) : null;
  const subtitle = formattedDate
    ? `Event on ${formattedDate}${lead?.event_time ? ` at ${lead.event_time}` : ""}.`
    : "A bespoke menu curated for your celebration";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Double border — fixed on every page */}
        <View style={styles.outerBorder} fixed />
        <View style={styles.innerBorder} fixed />

        {/* Top logos: brand logo (left) + outlet name (right) */}
        <View style={styles.topLogoRow}>
          <View style={styles.cornerLogoBlock}>
            {companyLogo && (
              <Image src={companyLogo} style={styles.cornerLogoImage} />
            )}
            <Text style={styles.cornerCompanyName}>
              BANQUET FIRST
            </Text>
          </View>
          <View style={styles.cornerLogoBlockRight}>
            {outletLogo && (
              <Image src={outletLogo} style={styles.cornerLogoImage} />
            )}
            <Text style={styles.cornerCompanyName}>
              {outletName.toUpperCase()}
            </Text>
          </View>
        </View>

        {/* Menu Title */}
        <Text style={styles.menuTitle}>
          {lead?.event_type || menu?.title || "Food Menu"}
        </Text>

        {/* Event Subtitle */}
        <Text style={styles.eventSubtitle}>{subtitle}</Text>

        {/* Diamond Divider */}
        <View style={styles.diamondDividerRow}>
          <View style={styles.diamondLine} />
          <View style={styles.diamond} />
          <View style={styles.diamondLine} />
        </View>

        {/* Stats Row */}
        <View style={styles.statsRow}>
          <Text style={styles.statText}>{totalDishes}  DISHES</Text>
          <Text style={styles.statText}>{totalCourses}  COURSES</Text>
          {totalVeg > 0 && <Text style={styles.statText}>{totalVeg}  VEG</Text>}
        </View>

        {/* Courses: Category → Subcategory → Items */}
        {categoriesToRender.map((group, idx) => {
          const catLimit = group.category.max_selection;
          const catItemCount = group.category.food_items.length;
          const isCatOverLimit = catLimit > 0 && catItemCount > catLimit;
          // Check if any subcategory is over limit
          const hasSubOverLimit = (group.category.subcategories || []).some((sub) => {
            const subLimit = sub.max_selection || 0;
            const subItems = group.subcategories.find(
              (sg) => sg.subcategory_name === sub.subcategory_name
            )?.items.length || 0;
            return subLimit > 0 && subItems > subLimit;
          });
          const hasExtraItems = isCatOverLimit || hasSubOverLimit;

          return (
          <View key={idx} style={styles.courseBlock} wrap={false}>
            <Text style={styles.courseLabel}>
              COURSE {String(idx + 1).padStart(2, "0")}
            </Text>

            <Text style={styles.courseName}>
              {group.category.category_name}
              {hasExtraItems && <Text style={{ color: "#ef4444" }}> *</Text>}
            </Text>

            {group.subcategories.map((subGroup, sIdx) => {
              // Find subcategory limit from the category's subcategories data
              const subMeta = group.category.subcategories?.find(
                (s) => s.subcategory_name === subGroup.subcategory_name
              );
              const subLimit = subMeta?.max_selection || 0;
              const subItemCount = subGroup.items.length;
              const isSubOverLimit = subLimit > 0 && subItemCount > subLimit;

              return (
              <View key={`sub-${sIdx}`}>
                <Text style={styles.subCategoryLabel}>
                  {subGroup.subcategory_name.toUpperCase()}
                </Text>

                {subGroup.items.map((item, i) => (
                  <View key={`item-${i}`} style={styles.foodItemRow}>
                    <View style={{ width: 14, height: 14, borderWidth: 2, borderColor: FOOD_ICON_COLORS[(item as any).item_icon] || GREEN, marginRight: 10, marginTop: 2, alignItems: "center", justifyContent: "center" }}>
                      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: FOOD_ICON_COLORS[(item as any).item_icon] || GREEN }} />
                    </View>
                    <View style={styles.foodItemContent}>
                      <Text style={styles.foodItemName}>{item.item_name}</Text>
                      {item.item_description ? (
                        <Text style={styles.foodItemDesc}>{item.item_description}</Text>
                      ) : null}
                    </View>
                    {item.food_type && (
                      <Text style={styles.foodItemTag}>{item.food_type.toUpperCase()}</Text>
                    )}
                  </View>
                ))}
              </View>
              );
            })}

            {group.orphanItems.length > 0 &&
              group.orphanItems.map((item, i) => (
                <View key={`orphan-${i}`} style={styles.foodItemRow}>
                  <View style={{ width: 14, height: 14, borderWidth: 2, borderColor: FOOD_ICON_COLORS[(item as any).item_icon] || GREEN, marginRight: 10, marginTop: 2, alignItems: "center", justifyContent: "center" }}>
                    <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: FOOD_ICON_COLORS[(item as any).item_icon] || GREEN }} />
                  </View>
                  <View style={styles.foodItemContent}>
                    <Text style={styles.foodItemName}>{item.item_name}</Text>
                    {item.item_description ? (
                      <Text style={styles.foodItemDesc}>{item.item_description}</Text>
                    ) : null}
                  </View>
                  {item.food_type && (
                    <Text style={styles.foodItemTag}>{item.food_type.toUpperCase()}</Text>
                  )}
                </View>
              ))}
          </View>
          );
        })}

        {/* Single note at page bottom if any category has extras */}
        {categoriesToRender.some((group) => {
          const catLimit = group.category.max_selection;
          const catItemCount = group.category.food_items.length;
          if (catLimit > 0 && catItemCount > catLimit) return true;
          return (group.category.subcategories || []).some((sub) => {
            const subLimit = sub.max_selection || 0;
            const subItems = group.subcategories.find(
              (sg) => sg.subcategory_name === sub.subcategory_name
            )?.items.length || 0;
            return subLimit > 0 && subItems > subLimit;
          });
        }) && (
          <View style={{ position: "absolute", bottom: 40, left: 0, right: 0, paddingHorizontal: 65 }} fixed>
            <Text style={{ fontSize: 7, color: "#64748B", textAlign: "center" }}>
              <Text style={{ fontWeight: "bold" }}>Note: </Text>Categories marked with an asterisk (<Text style={{ color: "#ef4444" }}>*</Text>) are optional and may be available at an additional cost.
            </Text>
          </View>
        )}
      </Page>
    </Document>
  );
};
